import React, { memo } from 'react';
import { X } from 'lucide-react'; // Importa l'icona X
import useGeoData from '../../../store/useGeoData';
import styles from './FixedFeaturePopup.module.css'; // Importa gli stili CSS Module

// Funzione helper per renderizzare i valori delle proprietà (simile alla logica del vecchio popup)
const renderPropertyValue = (key: string, value: any): React.ReactNode => {
  if (value === null) {
    return <em>null</em>;
  }
  if (value === undefined) {
    return <em>undefined</em>;
  }
  if (typeof value === 'string') {
    // Check for image URLs
    if (/\.(jpg|jpeg|png|gif|bmp|svg)(\?.*)?$/i.test(value)) {
      return <img src={value} alt={key} className={styles.propertyImage} />;
    }
    // Check for web URLs
    if (/^https?:\/\//i.test(value)) {
      return <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>;
    }
    return value; // Plain string
  }
  if (typeof value === 'object') {
    try {
      return <pre className={styles.propertyJson}>{JSON.stringify(value, null, 2)}</pre>;
    } catch (e) {
      return <em>[Oggetto non serializzabile]</em>;
    }
  }
  return String(value);
};


export const FixedFeaturePopup: React.FC = memo(() => {
  // Selettori per ottenere lo stato dal store Zustand
  const isVisible = useGeoData((state) => state.isFixedPopupVisible);
  const properties = useGeoData((state) => state.fixedPopupProperties);
  const selectedFields = useGeoData((state) => state.fixedPopupSelectedFields);
  const layerId = useGeoData((state) => state.fixedPopupLayerId); // Leggi layerId dallo stato
  const hidePopup = useGeoData((state) => state.hideFixedPopup);
  const getLayerById = useGeoData((state) => state.getLayerById); // Funzione per ottenere il layer

  // Recupera il nome del layer usando l'ID
  const layerName = layerId ? getLayerById(layerId)?.name : null;

  if (!isVisible || !properties) {
    return null; // Non renderizzare nulla se non è visibile o non ci sono proprietà
  }

  // Determina quali proprietà mostrare
  const propertiesToShow = selectedFields === null
    ? Object.entries(properties) // Mostra tutto se selectedFields è null
    : Object.entries(properties).filter(([key]) => selectedFields.includes(key)); // Filtra se selectedFields è un array

  // Filtra le proprietà che iniziano con '_' (considerate interne)
  const filteredPropertiesToShow = propertiesToShow.filter(([key]) => !key.startsWith('_'));

  const title = layerName ? `Proprietà: ${layerName}` : 'Proprietà Feature'; // Titolo dinamico

  return (
    <div className={styles.fixedPopupContainer}>
      {/* Nuovo contenitore per header */}
      <div className={styles.popupHeader}>
        <h4 className={styles.popupTitle}>{title}</h4>
        <button onClick={hidePopup} className={styles.closeButton} aria-label="Chiudi popup">
          <X size={18} /> {/* Usa l'icona X */}
        </button>
      </div>
      {filteredPropertiesToShow.length === 0 ? (
         <div className={styles.noPropertiesMessage}>
            {selectedFields === null && Object.keys(properties).filter(k => !k.startsWith('_')).length > 0
              ? <em>Nessuna proprietà utente da mostrare (solo interne).</em>
              : selectedFields === null
              ? <em>Nessuna proprietà disponibile.</em>
              : selectedFields.length === 0
              ? <em>Nessun campo selezionato per il popup.</em>
              : <em>Nessuna delle proprietà selezionate trovata.</em>
            }
         </div>
      ) : (
        <div className={styles.propertiesContainer}> {/* Usa il nuovo contenitore */}
          {filteredPropertiesToShow.map(([key, value]) => (
            <div key={key} className={styles.propertyRow}> {/* Usa la riga per proprietà */}
              <div className={styles.propertyKey}>{key}</div> {/* Applica stile alla chiave */}
              <div className={styles.propertyValue}>{renderPropertyValue(key, value)}</div> {/* Applica stile al valore */}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

FixedFeaturePopup.displayName = 'FixedFeaturePopup';